import { type DefinitionNode } from 'graphql';
import { type GraphQLTaggedNode } from 'react-relay';
import { type ConcreteRequest } from 'relay-runtime';

export type RelayOperationVariables = Record<string, any> | undefined;

type RelayArgumentDefinition = {
	kind?: string;
	name?: string;
	defaultValue?: unknown;
};

/**
 * Argument definitions declared by a Relay artifact — a `ConcreteRequest`'s normalization operation
 * or a `ReaderFragment`. Apollo `DocumentNode`s carry none, so they resolve to `null`.
 */
function getRelayArgumentDefinitions(
	node: GraphQLTaggedNode | null | undefined,
): readonly RelayArgumentDefinition[] | null {
	const artifact = node as
		| {
				operation?: { argumentDefinitions?: readonly RelayArgumentDefinition[] };
				argumentDefinitions?: readonly RelayArgumentDefinition[];
		  }
		| null
		| undefined;

	return artifact?.operation?.argumentDefinitions ?? artifact?.argumentDefinitions ?? null;
}

/**
 * Fills in the default values a Relay artifact declares for variables the caller left unset.
 *
 * Relay applies these defaults itself before it normalizes or reads a payload
 * (`createOperationDescriptor` -> `getOperationVariables`), so field storage keys in the store are
 * built from the *resolved* variables. A query declaring `$first: Int = 20` normalizes its root
 * field under `field(...,first:20)` even when the caller only passed `{ contentId, cloudId }`. Any
 * interop code that computes storage keys straight from the caller's variables therefore targets a
 * *different* record than the one Relay wrote and the UI subscribes to — a silent no-op write.
 *
 * Unlike Relay's `getOperationVariables`, this is purely **additive**: it never drops variables that
 * the artifact does not declare, and never overrides a value the caller supplied. Interop callers
 * legitimately pass variables beyond the artifact's own definitions (for example `@include`/`@skip`
 * condition flags when writing through hand-built selection trees), and discarding those would
 * change which fields get written. The `!= null` checks match Relay's own semantics: an explicit
 * `null` or `undefined` falls back to the declared default.
 *
 * Returns the caller's object unchanged when there is nothing to fill in, keeping this cheap on the
 * cache-write hot path.
 */
export function getRelayOperationVariablesWithDefaults(
	node: GraphQLTaggedNode | null | undefined,
	variables?: RelayOperationVariables,
): Record<string, any> {
	const provided: Record<string, any> = variables ?? {};
	const argumentDefinitions = getRelayArgumentDefinitions(node);

	if (!argumentDefinitions || argumentDefinitions.length === 0) {
		return provided;
	}

	let resolved: Record<string, any> | undefined;
	for (const definition of argumentDefinitions) {
		const name = definition?.name;
		// `defaultValue == null` means the artifact declares no default (Relay emits `null` here for
		// undefaulted variables), so there is nothing to fill in. Falsy-but-present defaults such as
		// `false`, `0` and `''` are real defaults and must still be applied.
		if (typeof name !== 'string' || definition.defaultValue == null) {
			continue;
		}
		if (provided[name] != null) {
			continue;
		}

		resolved = resolved ?? { ...provided };
		resolved[name] = definition.defaultValue;
	}

	return resolved ?? provided;
}

function sanitizeOperationKey(operationName: string): string {
	return operationName.replace(/[^\-.\w]/g, '_');
}

export function getRelayOperationName(query: GraphQLTaggedNode): string | null {
	if ('params' in query) {
		return (query as ConcreteRequest).params?.name ?? null;
	}

	if ('definitions' in query) {
		const definition = (query as { definitions: readonly DefinitionNode[] }).definitions?.find(
			(d): d is DefinitionNode & { name?: { value: string } } =>
				d.kind === 'OperationDefinition' && 'name' in d,
		);
		return definition?.name?.value ?? null;
	}

	return null;
}

export function getRelayOperationKey(
	operationName: string | null | undefined,
	_variables?: RelayOperationVariables,
): string | null {
	if (!operationName) {
		return null;
	}

	return sanitizeOperationKey(operationName);
}
